import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { fetchAllMasterData } from "@/store/slices/masterSlice";
import { useDispatch, useSelector } from "react-redux";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useLocation, useParams } from "react-router-dom";
import {
  fetchNewShuttleLess,
  fetchNewShuttleLessAvailable,
  resetNewLoomSliceState,
  setCurrentTab,
} from "../../store/slices/newLoomSlice";
import PersonalDetails from "./NewLoomForms/PersonalDetails";
import LoomDetails from "./NewLoomForms/LoomDetails";
import BankDetails from "./NewLoomForms/BankDetails";
import PriorityDetails from "./NewLoomForms/PriorityDetails";
import DeclarationDetails from "./NewLoomForms/DeclarationDetails";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import UploadRequirementsDialog from "../../components/common/UploadRequirementsDialog";
import { Button } from "../../components/ui/button";
import NewPurchasedDetails from "./NewLoomForms/NewPurchasedDetails";
import SanctionDetails from "./NewLoomForms/SanctionDetails";
import PurchaseDeclarationDetails from "./NewLoomForms/PurchaseDeclarationDetails";
import HowToApplyNewShuttleLess from "../../assets/pdf/how_to_apply_newshuttleless.pdf";
import EmpanelSupplierList from "../../assets/pdf/empanel_list.pdf";

const NewLoomForm = () => {
  const { current_tab, data, data_loading, user_initiated_nav } = useSelector(
    (state) => state.newLoom
  );

  const {
    districts = [],
    communities = [],
    loading,
  } = useSelector((state) => state.master || {});
  const dispatch = useDispatch();
  const location = useLocation();
  const { id } = useParams();
  const [completedTabs, setCompletedTabs] = useState(new Set());
  const [declarationCompleted, setDeclarationCompleted] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  useEffect(() => {
    setOpenModal(true);
  }, []);

  const tabsOrder = [
    "personal",
    "loom",
    "bank",
    "priority",
    "purchase_detail",
    "declaration",
    ...(data?.sanction?.status == "provisional_sanction_recommended"
      ? ["sanction_details"]
      : []),
    ...(data?.purchase_details?.already_purchase == "No" &&
    data?.sanction?.status == "provisional_sanction_recommended"
      ? ["after_purchase_detail", "purchase_declaration"]
      : []),
  ];

  useEffect(() => {
    dispatch(resetNewLoomSliceState());
    dispatch(fetchAllMasterData());
    dispatch(fetchNewShuttleLessAvailable());
    if (id) {
      dispatch(fetchNewShuttleLess(id));
    }
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      const searchParams = new URLSearchParams(location.search);
      const tabParam = searchParams.get("tab");
      const newCompletedTabs = new Set();
      let nextTab = "personal";

      if (data?.id) {
        newCompletedTabs.add("personal");
        nextTab = "loom";
      }

      if (data?.id && data?.beneficiary?.premises_type) {
        newCompletedTabs.add("loom");
        nextTab = "bank";
      }

      if (
        data?.id &&
        data?.beneficiary?.premises_type &&
        data?.bank_details?.bank_name
      ) {
        newCompletedTabs.add("bank");
        nextTab = "priority";
      }

      if (
        data?.id &&
        data?.beneficiary?.premises_type &&
        data?.bank_details?.bank_name &&
        data?.additional_details?.is_tie_up_made
      ) {
        newCompletedTabs.add("priority");
        nextTab = "purchase_detail";
      }

      if (
        data?.id &&
        data?.beneficiary?.premises_type &&
        data?.bank_details?.bank_name &&
        data?.additional_details?.is_tie_up_made &&
        data?.purchase_details?.already_purchase
      ) {
        newCompletedTabs.add("purchase_detail");
        nextTab = "declaration";
      }

      if (
        data?.id &&
        data?.beneficiary?.premises_type &&
        data?.bank_details?.bank_name &&
        data?.additional_details?.is_tie_up_made &&
        data?.purchase_details?.already_purchase &&
        data?.declaration?.terms_accepted
      ) {
        newCompletedTabs.add("declaration");
        if (data?.sanction?.status == "provisional_sanction_recommended") {
          nextTab = "sanction_details";
        }
      }

      if (
        data?.id &&
        data?.beneficiary?.premises_type &&
        data?.bank_details?.bank_name &&
        data?.additional_details?.is_tie_up_made &&
        data?.purchase_details?.already_purchase &&
        data?.declaration?.terms_accepted &&
        data?.sanction?.status == "provisional_sanction_recommended" &&
        data?.sanction?.is_user_accept
      ) {
        newCompletedTabs.add("sanction_details");
        if (data?.purchase_details?.already_purchase == "No") {
          nextTab = "after_purchase_detail";
        }
      }

      if (
        data?.id &&
        data?.beneficiary?.premises_type &&
        data?.bank_details?.bank_name &&
        data?.additional_details?.is_tie_up_made &&
        data?.purchase_details?.already_purchase &&
        data?.declaration?.terms_accepted &&
        data?.sanction?.status == "provisional_sanction_recommended" &&
        data?.sanction?.is_user_accept
      ) {
        newCompletedTabs.add("sanction_details");
        if (data?.purchase_details?.already_purchase == "No") {
          nextTab = "after_purchase_detail";
        }
      }

      if (
        data?.id &&
        data?.beneficiary?.premises_type &&
        data?.bank_details?.bank_name &&
        data?.additional_details?.is_tie_up_made &&
        data?.purchase_details?.already_purchase == "No" &&
        data?.purchase_details?.purchase_proof &&
        data?.declaration?.terms_accepted &&
        data?.sanction?.status == "provisional_sanction_recommended" &&
        data?.sanction?.is_user_accept
      ) {
        newCompletedTabs.add("after_purchase_detail");
        nextTab = "purchase_declaration";
      }

      setCompletedTabs(newCompletedTabs);
      if (tabParam && tabsOrder.includes(tabParam)) {
        dispatch(setCurrentTab(tabParam));
      } else if (!user_initiated_nav) {
        dispatch(setCurrentTab(nextTab));
      }
    }
  }, [data, dispatch, location.search, user_initiated_nav]);

  const getCompletionPercentage = () => {
    const completedCount = completedTabs.size;
    const totalTabs = tabsOrder.length;
    return Math.round((completedCount / totalTabs) * 100);
  };

  const isTabDisabled = (tabId) => {
    const tabIndex = tabsOrder.indexOf(tabId);
    if (tabIndex === 0) return false;

    for (let i = 0; i < tabIndex; i++) {
      if (!completedTabs.has(tabsOrder[i])) {
        return true;
      }
    }

    return false;
  };

  const handleTabComplete = (tabId) => {
    setCompletedTabs((prev) => new Set([...prev, tabId]));
  };

  const showCheckmark = (tabId) => {
    return completedTabs.has(tabId);
  };

  const uploads_array = [
    { name: "Applicant Photo", type: "JPG, JPEG, PNG max size 2MB" },
    { name: "Aadhaar Uploads", type: "PDF, PNG, JPG, JPEG max size 2MB" },
    {
      name: "GPS Location of the Loom Shed",
      type: "PNG, JPG, JPEG max size 2MB",
      notes:
        "Install the application in your android device from this link -> https://play.google.com/store/apps/details?id=com.gpsmapcamera.geotagginglocationonphoto and capture the photo to upload",
    },
    {
      name: "List of Empanel Supplier",
      type: "Important Note",
      notes:
        "The suppliers included in this empanelled list are recommended sources for the purchase of New Rapier Loom. Adherence to this list facilitates the process for obtaining provisional sanction.",
      file: "supplier_list",
    },
    {
      name: "Undertaking certificate for not selling",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
      notes:
        "Download Undertaking Certificate and print in 20 rupees stamp paper",
      file: "undertaking",
    },
    {
      name: "Electricity Bill",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    {
      name: "Any Other Scheme Documents",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    {
      name: "Udyog Aadhaar",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    {
      name: "SSI / MSME Registration",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    { name: "Invoice Uploads", type: "PDF, PNG, JPG, JPEG max size 2MB" },
    { name: "Bank Passbook", type: "PDF, PNG, JPG, JPEG max size 2MB" },
    {
      name: "Scheme Documents",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    { name: "Applicant Signature", type: "PNG, JPG, JPEG max size 2MB" },
    {
      name: "Export Oriented Uploads (optional)",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    {
      name: "Detailed Project Report Upload (optional)",
      type: "PDF, PNG, JPG, JPEG max size 5MB",
    },
    {
      name: "PAN Document Upload",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    {
      name: "GST Document Upload",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    {
      name: "Partnership Deed Upload (optional)",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    {
      name: "Declaration Document",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
      notes:
        "Download the declaration document, take a printout, and sign it with the date and place and Upload.",
      file: "declaration",
    },
    {
      name: "Other Supporting Documents",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
  ];

  const handleDownload = (fileUrl, fileName) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  return (
    <div className="max-w-6xl mx-auto p-0 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between gap-10 mb-10">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-primary">
            Purchase of New Rapier Loom / Replacement of <br />
            existing Low Speed Rapier Looms
          </h1>
          <p className="text-gray-700 text-sm my-5">
            <b>Note: </b>The suppliers included in this empanelled list are recommended
            sources for the purchase of New Rapier Loom. <br /> Adherence to
            this list facilitates the process for obtaining provisional
            sanction. <br />
            <b
              className="text-red-500 cursor-pointer"
              onClick={() =>
                handleDownload(EmpanelSupplierList, `supplier_list.pdf`)
              }
            >
              Click here To download Empanel Supplier List
            </b>
          </p>
          {data?.id && (
            <p className="text-muted-foreground">
              Application Number : {data?.application_no || "N/A"}
            </p>
          )}
        </div>
        <div className="space-y-2 flex flex-col justify-end gap-3">
          <Button
            type="button"
            size="sm"
            variant="default"
            className="text-xs"
            onClick={() =>
              handleDownload(HowToApplyNewShuttleLess, `how_to_apply.pdf`)
            }
          >
            How To Apply ?
          </Button>
          <Button
            type="button"
            size="sm"
            variant="default"
            className="text-xs"
            onClick={() => setOpenModal(true)}
          >
            Application Requirements
          </Button>
        </div>
      </div>
      {loading || data_loading ? (
        <div className="flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Application Progress</span>
              <span>{getCompletionPercentage()}% Complete</span>
            </div>
            <Progress value={getCompletionPercentage()} className="h-2" />
          </div>

          <Tabs
            value={current_tab}
            onValueChange={(value) => dispatch(setCurrentTab(value))}
            className="space-y-6"
          >
            <TabsList className="w-full flex flex-wrap justify-evenly">
              <TabsTrigger
                value="personal"
                className="flex items-center gap-1"
                disabled={
                  isTabDisabled("personal") ||
                  (data?.status &&
                    data?.status != "started" &&
                    data?.status != "incomplete")
                }
              >
                Personal
                <br /> Details
                {showCheckmark("personal") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="loom"
                className="flex items-center gap-1"
                disabled={
                  isTabDisabled("loom") ||
                  (data?.status &&
                    data?.status != "started" &&
                    data?.status != "incomplete")
                }
              >
                Beneficiary and
                <br />
                Loom Details
                {showCheckmark("loom") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="bank"
                className="flex items-center gap-1"
                disabled={
                  isTabDisabled("bank") ||
                  (data?.status && data?.status != "incomplete")
                }
              >
                Bank
                <br /> Details
                {showCheckmark("bank") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="priority"
                className="flex items-center gap-1"
                disabled={
                  isTabDisabled("priority") ||
                  (data?.status && data?.status != "incomplete")
                }
              >
                Additional
                <br /> Details
                {showCheckmark("priority") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="purchase_detail"
                className="flex items-center gap-1"
                disabled={
                  isTabDisabled("purchase_detail") ||
                  (data?.status && data?.status != "incomplete")
                }
              >
                Purchase <br /> Details
                {showCheckmark("purchase_detail") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="declaration"
                className="flex items-center gap-1"
                disabled={
                  isTabDisabled("declaration") ||
                  (data?.status && data?.status != "incomplete")
                }
              >
                Declaration
                {showCheckmark("declaration") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              {data?.sanction?.status == "provisional_sanction_recommended" && (
                <>
                  <TabsTrigger
                    value="sanction_details"
                    className="flex items-center gap-1"
                    disabled={isTabDisabled("sanction_details")}
                  >
                    Sanction <br /> Details
                    {showCheckmark("sanction_details") && (
                      <span className="ml-1 text-green-600">✓</span>
                    )}
                  </TabsTrigger>
                  {data?.purchase_details?.already_purchase == "No" && (
                    <>
                      <TabsTrigger
                        value="after_purchase_detail"
                        className="flex items-center gap-1"
                        disabled={isTabDisabled("after_purchase_detail")}
                      >
                        After Sanction <br />
                        Purchase Details
                        {showCheckmark("after_purchase_detail") && (
                          <span className="ml-1 text-green-600">✓</span>
                        )}
                      </TabsTrigger>
                      <TabsTrigger
                        value="purchase_declaration"
                        className="flex items-center gap-1"
                        disabled={isTabDisabled("purchase_declaration")}
                      >
                        Purchase Declaration
                        {showCheckmark("purchase_declaration") && (
                          <span className="ml-1 text-green-600">✓</span>
                        )}
                      </TabsTrigger>
                    </>
                  )}
                </>
              )}
            </TabsList>

            <TabsContent value="personal">
              <PersonalDetails
                districts={districts}
                communities={communities}
                data={data}
                onTabComplete={() => handleTabComplete("personal")}
              />
            </TabsContent>
            <TabsContent value="loom">
              <LoomDetails
                districts={districts}
                communities={communities}
                data={data}
                file_name={"new_shuttleless"}
                onTabComplete={() => handleTabComplete("loom")}
              />
            </TabsContent>
            <TabsContent value="bank">
              <BankDetails
                districts={districts}
                communities={communities}
                data={data}
                onTabComplete={() => handleTabComplete("bank")}
              />
            </TabsContent>
            <TabsContent value="priority">
              <PriorityDetails
                districts={districts}
                communities={communities}
                data={data}
                onTabComplete={() => handleTabComplete("priority")}
              />
            </TabsContent>
            <TabsContent value="purchase_detail">
              <NewPurchasedDetails
                already_purchased={true}
                districts={districts}
                communities={communities}
                data={data}
                file_name="new_shuttleless"
                onTabComplete={() => handleTabComplete("purchase_detail")}
              />
            </TabsContent>
            <TabsContent value="declaration">
              <DeclarationDetails
                districts={districts}
                communities={communities}
                data={data}
                onTabComplete={() => handleTabComplete("declaration")}
              />
            </TabsContent>
            <TabsContent value="sanction_details">
              <SanctionDetails
                data={data}
                onTabComplete={() => handleTabComplete("sanction_details")}
              />
            </TabsContent>
            <TabsContent value="after_purchase_detail">
              <NewPurchasedDetails
                already_purchased={false}
                districts={districts}
                communities={communities}
                data={data}
                file_name="new_shuttleless"
                onTabComplete={() => handleTabComplete("purchase_detail")}
              />
            </TabsContent>
            <TabsContent value="purchase_declaration">
              <PurchaseDeclarationDetails data={data} />
            </TabsContent>
          </Tabs>
          <UploadRequirementsDialog
            openModal={openModal}
            setOpenModal={setOpenModal}
            uploads_array={uploads_array}
          />
        </>
      )}
    </div>
  );
};

export default NewLoomForm;
